// The employer this entry is about, and the person we would call about it.
//
// Read-only by design: both were chosen in an earlier step, and both are
// changed by going back to that step rather than by editing text here.
//
// The contact half is shown whenever it can be edited, including when there is
// no contact yet — then it says so and offers to add one. Hiding it in that
// case is how an entry ends up with a contact the driver can neither see nor
// change: reopening an employer saved without one left nothing on screen to
// tap, and reopening one saved with a contact showed nothing either.

import ApplicationAddressInput from '../../components/ApplicationAddressInput'
import type { Errors } from '../lib/validation'

import { SectionHeader } from './SectionHeader'

export interface EmployerCardProps {
  name: string
  /** DOT number, address — whatever identifies which company this is. */
  facts?: Array<string | null | undefined>
  onChange: () => void
  /**
   * Set when the driver typed the employer rather than picking it. The registry
   * supplies a name and an address; a typed one has neither, so the form has to
   * ask — without these fields the entry can never satisfy validation and Save
   * is refused with nothing on screen to fix.
   */
  editable?: boolean
  city?: string | null
  state?: string | null
  dotNumber?: string | null
  onFieldChange?: (patch: {
    companyName?: string
    dotNumber?: string
    city?: string
    state?: string
    street?: string
    zipCode?: string
  }) => void
  errors?: Errors
  contact?: {
    name?: string | null
    phone?: string | null
    email?: string | null
  } | null
  onEditContact?: () => void
}

/** Phone then email, each on its own line, skipping what we do not hold. */
const contactLines = (contact: EmployerCardProps['contact']): string[] =>
  [contact?.phone, contact?.email].filter((value): value is string =>
    Boolean(value && value.trim())
  )

const field =
  'w-full border-b border-doubleNickel-gray-300 bg-transparent py-1.5 text-[15px] text-doubleNickel-gray-900 outline-none placeholder:text-doubleNickel-gray-400'

const label = 'mb-1 text-[11px] font-medium text-doubleNickel-gray-600'

export const EmployerCard = ({
  name,
  facts,
  onChange,
  editable = false,
  city,
  state,
  dotNumber,
  onFieldChange,
  errors = {},
  contact,
  onEditContact,
}: EmployerCardProps) => {
  const details = facts?.filter((fact): fact is string =>
    Boolean(fact && fact.trim())
  )
  const lines = contactLines(contact)
  const employerProblems = [
    errors.companyName,
    errors.city,
    errors.state,
  ].filter((message): message is string => Boolean(message))
  const hasContact = Boolean(contact && (contact.name || lines.length > 0))
  const contactProblems = [errors.phone, errors.email].filter(
    (message): message is string => Boolean(message)
  )

  return (
    <div className="rounded-[13px] border border-doubleNickel-gray-200 bg-white px-3 py-2.5">
      {editable ? (
        <div className="flex flex-col gap-2.5">
          <div className="flex items-start justify-between gap-2.5">
            <SectionHeader title="Employer" />
            <button
              type="button"
              onClick={onChange}
              className="flex-none text-[12px] font-semibold text-doubleNickel-brand-600"
            >
              Search instead
            </button>
          </div>
          <div>
            <div className={label}>
              Company name
              <span className="ml-0.5 text-doubleNickel-error-600">*</span>
            </div>
            <input
              type="text"
              aria-label="Company name"
              value={name}
              placeholder="Who did you drive for?"
              onChange={(event) =>
                onFieldChange?.({ companyName: event.target.value })
              }
              className={field}
            />
            {errors.companyName && (
              <div className="mt-1 text-[11px] text-doubleNickel-error-700">
                {errors.companyName}
              </div>
            )}
          </div>
          <div>
            <div className={label}>DOT number</div>
            <input
              type="text"
              inputMode="numeric"
              aria-label="DOT number"
              value={dotNumber ?? ''}
              placeholder="If you know it"
              onChange={(event) =>
                onFieldChange?.({ dotNumber: event.target.value })
              }
              className={field}
            />
            {errors.dotNumber && (
              <div className="mt-1 text-[11px] text-doubleNickel-error-700">
                {errors.dotNumber}
              </div>
            )}
          </div>
          <div>
            <div className={label}>
              City and state
              <span className="ml-0.5 text-doubleNickel-error-600">*</span>
            </div>
            {/*
              One lookup instead of two fields. Typing a city and separately
              picking a state invites "seattle, AK" — a real place and a real
              state that have nothing to do with each other, which then goes out
              on a verification request.
            */}
            <ApplicationAddressInput
              // An address object, not a string — the component formats it
              value={{ city: city ?? '', state: state ?? '' }}
              // Cities and towns only. The default asks Google for street
              // addresses, which is why searching "seattle" returned Seattle
              // Slew Drive in New Jersey.
              types={['locality', 'administrative_area_level_3']}
              placeholder="Start typing a city"
              error={errors.city ?? errors.state}
              handleChange={(value: {
                street?: string
                city?: string
                state?: string
                zipCode?: string
              }) =>
                onFieldChange?.({
                  city: value.city ?? '',
                  state: value.state ?? '',
                  street: value.street ?? '',
                  zipCode: value.zipCode ?? '',
                })
              }
            />
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-2.5">
          <div className="min-w-0">
            <div className="truncate text-[15px] font-semibold text-doubleNickel-gray-900">
              {name}
            </div>
            {details?.length ? (
              <div className="mt-0.5 text-[12px] text-doubleNickel-gray-600">
                {details.join(' · ')}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            aria-label="Edit employer"
            onClick={onChange}
            className="flex-none text-[12px] font-semibold text-doubleNickel-brand-600"
          >
            Edit
          </button>
        </div>
      )}

      {/*
        An entry created elsewhere can reach the form missing a city or state.
        Closed does not mean silent: Save would otherwise refuse over fields
        that are not on screen.
      */}
      {!editable && employerProblems.length > 0 && (
        <div className="mt-1 text-[11px] text-doubleNickel-error-700">
          {employerProblems.join(' ')}
        </div>
      )}

      {onEditContact && (
        <div className="mt-2.5 border-t border-doubleNickel-gray-100 pt-2">
          <SectionHeader title="Contact" />
          <div className="mt-1 flex items-start justify-between gap-2.5">
            <div className="min-w-0">
              {hasContact ? (
                <>
                  {contact?.name && (
                    <div className="truncate text-[13px] font-semibold text-doubleNickel-gray-900">
                      {contact.name}
                    </div>
                  )}
                  {lines.map((value) => (
                    <div
                      key={value}
                      className="truncate text-[12px] text-doubleNickel-gray-600"
                    >
                      {value}
                    </div>
                  ))}
                </>
              ) : (
                <div className="text-[12px] text-doubleNickel-gray-500">
                  No contact
                </div>
              )}
            </div>
            <button
              type="button"
              aria-label="Edit contact"
              onClick={onEditContact}
              className="flex-none text-[12px] font-semibold text-doubleNickel-brand-600"
            >
              Edit
            </button>
          </div>
          {contactProblems.length > 0 && (
            <div
              role="alert"
              className="mt-1 text-[11px] text-doubleNickel-error-700"
            >
              {contactProblems.join(' ')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
